/**
 * Custom fabric - sitewide cart backup and restore.
 *
 * A custom fabric line carries uploaded artwork, so losing the cart (expired cookie, cleared session, another device
 * flow) is costly. This module keeps a device-only backup of the cart in localStorage and, when the cart comes back
 * empty without the customer having emptied it, offers to restore it.
 *
 * Intentional vs accidental empty:
 *  - Any remove / change / update / clear request (or a click on a remove control) marks a "pending mutation" for
 *    60 seconds. An empty cart inside that window is intentional: the backup is cleared.
 *  - Starting checkout records the backup signature. If the cart later comes back empty with the same contents, the
 *    order was most likely placed, so the backup is cleared instead of offered.
 *  - Backups expire after 30 days.
 */
const VERSION = 1;
const BACKUP_KEY = 'cf_cart_backup_v1';
const CLEAR_KEY = 'cf_cart_intentional_clear_v1';
const PENDING_KEY = 'cf_cart_pending_mutation_v1';
const CHECKOUT_KEY = 'cf_cart_checkout_v1';
const DISMISS_KEY = 'cf_cart_dismissed_v1';
const PENDING_WINDOW = 60 * 1000;
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const SYNC_INTERVAL = 45 * 1000;
const CART_URL_PATTERN = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

const configNode = document.getElementById('cf-recovery-config');
const config = configNode ? JSON.parse(configNode.textContent) : null;

// Shopify's `t` filter HTML-escapes translations ("&" arrives as "&amp;"); the toast writes plain text, so decode.
if (config && config.strings) {
  const decoder = document.createElement('textarea');
  Object.keys(config.strings).forEach((key) => {
    decoder.innerHTML = config.strings[key];
    config.strings[key] = decoder.value;
  });
}

/* ---------------------------------------------------------------- storage (never throws) */

const store = {
  get(area, key) {
    try {
      const raw = window[area].getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  },
  set(area, key, value) {
    try {
      window[area].setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage full or blocked */
    }
  },
  remove(area, key) {
    try {
      window[area].removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }
};

function track(name, payload = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, payload);
  } catch (error) {
    /* ignore */
  }
}

/* ---------------------------------------------------------------- cart helpers */

function cleanProperties(properties) {
  if (!properties) return {};
  const result = {};
  Object.keys(properties).forEach((key) => {
    if (properties[key] !== null && properties[key] !== undefined) result[key] = properties[key];
  });
  return result;
}

function toBackupItems(items) {
  return items.map((item) => {
    const entry = { id: item.variant_id || item.id, quantity: item.quantity, properties: cleanProperties(item.properties) };
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      entry.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }
    return entry;
  });
}

function signatureOf(items) {
  return items
    .map((item) => `${item.id}x${item.quantity}:${JSON.stringify(item.properties || {})}`)
    .sort()
    .join('|');
}

async function fetchCart() {
  const response = await originalFetch(`${config.routes.root}cart.js`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) throw new Error('cart.js failed');
  return response.json();
}

async function postJson(path, body) {
  const response = await originalFetch(`${config.routes.root}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) throw new Error(data.description || 'cart request failed');
  return data;
}

/* ---------------------------------------------------------------- mutation tracking */

function markPending(type) {
  store.set('localStorage', PENDING_KEY, { type, at: Date.now() });
}

function markCheckout() {
  const backup = store.get('localStorage', BACKUP_KEY);
  if (backup) store.set('localStorage', CHECKOUT_KEY, { signature: backup.signature, at: Date.now() });
}

let syncTimer = null;
function scheduleSync(delay = 350) {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(sync, delay);
}

function cartMutationType(url) {
  const match = String(url || '').match(CART_URL_PATTERN);
  return match ? match[1] : null;
}

const originalFetch = window.fetch.bind(window);

function hookFetch() {
  window.fetch = function cfFetch(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const type = cartMutationType(url);
    if (type && type !== 'add') markPending(type);
    const promise = originalFetch(input, init);
    if (type) {
      promise
        .then((response) => {
          if (response.ok) scheduleSync();
        })
        .catch(() => {});
    }
    return promise;
  };
}

function hookXhr() {
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function cfOpen(method, url, ...rest) {
    this.__cfCartType = cartMutationType(url);
    return open.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function cfSend(...args) {
    const type = this.__cfCartType;
    if (type) {
      if (type !== 'add') markPending(type);
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) scheduleSync();
      });
    }
    return send.apply(this, args);
  };
}

const REMOVE_SELECTOR = [
  'a[href*="/cart/change"]',
  'a[href*="/cart/clear"]',
  '[data-action="remove-item"]',
  '[data-action*="remove"]',
  '[data-cart-remove]',
  '.line-item__remove',
  '.mini-cart__quantity-remove',
  'button[name="clear"]'
].join(',');

function bindDomSignals() {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target.closest ? event.target : event.target.parentElement;
      if (!target) return;
      if (target.closest(REMOVE_SELECTOR)) markPending('remove');
      if (target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"], shopify-accelerated-checkout, .shopify-payment-button')) {
        markCheckout();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.action) return;
      const path = new URL(form.action, window.location.href).pathname;
      if (!/\/cart(\/|$)/.test(path) || /\/cart\/add/.test(path)) return;
      const submitter = event.submitter;
      if ((submitter && submitter.name === 'checkout') || form.querySelector('[name="checkout"]:focus')) markCheckout();
      else markPending('update');
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const form = event.target && event.target.closest ? event.target.closest('form[action*="/cart"]') : null;
      if (form && !/\/cart\/add/.test(form.getAttribute('action') || '')) markPending('update');
    },
    true
  );
}

/* ---------------------------------------------------------------- sync */

let syncing = null;

function sync() {
  if (syncing) return syncing;
  syncing = (async () => {
    try {
      const cart = await fetchCart();
      if (cart.item_count > 0) saveBackup(cart);
      else handleEmpty();
    } catch (error) {
      /* offline or blocked - try again later */
    } finally {
      syncing = null;
    }
  })();
  return syncing;
}

function saveBackup(cart) {
  const items = toBackupItems(cart.items || []);
  const backup = {
    version: VERSION,
    savedAt: Date.now(),
    itemCount: cart.item_count,
    totalPrice: cart.total_price,
    currency: cart.currency,
    note: cart.note || '',
    attributes: cart.attributes || {},
    signature: signatureOf(items),
    items
  };
  store.set('localStorage', BACKUP_KEY, backup);
  store.remove('localStorage', CLEAR_KEY);
  hideToast();
}

function handleEmpty() {
  const backup = store.get('localStorage', BACKUP_KEY);
  if (!backup || backup.version !== VERSION || !Array.isArray(backup.items) || !backup.items.length) return;

  if (Date.now() - backup.savedAt > MAX_AGE) {
    store.remove('localStorage', BACKUP_KEY);
    return;
  }

  const pending = store.get('localStorage', PENDING_KEY);
  if (pending && pending.type !== 'add' && Date.now() - pending.at < PENDING_WINDOW) {
    store.remove('localStorage', BACKUP_KEY);
    store.set('localStorage', CLEAR_KEY, { at: Date.now() });
    return;
  }

  const checkout = store.get('localStorage', CHECKOUT_KEY);
  if (checkout && checkout.signature === backup.signature) {
    store.remove('localStorage', BACKUP_KEY);
    store.remove('localStorage', CHECKOUT_KEY);
    return;
  }

  const dismissed = store.get('sessionStorage', DISMISS_KEY);
  if (dismissed && dismissed.signature === backup.signature) return;

  showToast(backup);
}

/* ---------------------------------------------------------------- toast */

let toast = null;

function injectStyles() {
  if (document.getElementById('cf-recovery-styles')) return;
  const style = document.createElement('style');
  style.id = 'cf-recovery-styles';
  // Colors follow Theme settings > Custom fabric
  const c = Object.assign(
    {
      accent: '#7a263a', accentTint: '#efe3e5', button: '#7a263a', buttonText: '#ffffff', buttonHover: '#5d1c2b',
      buttonBorder: '#7a263a', border: '#ded8d2', shadow: false, ink: '#202020', muted: '#6f6862', card: '#ffffff', danger: '#b42318'
    },
    config.colors || {}
  );
  style.textContent = `
    .cf-recovery{position:fixed;left:50%;bottom:16px;z-index:9999;width:min(620px,calc(100% - 28px));transform:translateX(-50%);
      display:flex;gap:14px;align-items:flex-start;padding:16px 18px;background:${c.card};color:${c.ink};border:1px solid ${c.border};
      border-radius:16px;box-shadow:0 18px 55px rgba(18,24,28,.22);font:15px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;
      animation:cf-recovery-in .25s ease}
    .cf-recovery[hidden]{display:none}
    .cf-recovery__icon{flex:0 0 40px;width:40px;height:40px;display:inline-flex;align-items:center;justify-content:center;border-radius:50%;
      background:${c.accentTint};color:${c.accent}}
    .cf-recovery__body{flex:1 1 auto;min-width:0}
    .cf-recovery__text{margin:0}
    .cf-recovery__source{margin:4px 0 0;font-size:12px;color:${c.muted}}
    .cf-recovery__actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
    .cf-recovery__restore{min-height:44px;padding:10px 16px;font:inherit;font-weight:800;color:${c.buttonText};background:${c.button};
      border:2px solid ${c.buttonBorder};border-radius:9px;box-shadow:${c.shadow ? `3px 3px 0 ${c.buttonBorder}` : 'none'};cursor:pointer}
    .cf-recovery__restore:hover{background:${c.buttonHover};border-color:${c.buttonHover}}
    .cf-recovery__restore:active{transform:translate(2px,2px);box-shadow:none}
    .cf-recovery__restore[disabled]{opacity:.7;cursor:progress}
    .cf-recovery__dismiss{min-height:44px;padding:10px 12px;font:inherit;font-weight:700;color:${c.ink};background:none;border:0;
      text-decoration:underline;cursor:pointer}
    .cf-recovery__error{margin:8px 0 0;font-size:13px;font-weight:700;color:${c.danger}}
    .cf-recovery :focus-visible{outline:3px solid ${c.accent};outline-offset:2px}
    @keyframes cf-recovery-in{from{opacity:0;transform:translate(-50%,16px)}to{opacity:1;transform:translate(-50%,0)}}
    @media (prefers-reduced-motion: reduce){.cf-recovery{animation:none}}`;
  document.head.appendChild(style);
}

function showToast(backup) {
  if (toast && !toast.hidden) return;
  injectStyles();
  const s = config.strings;
  const body = backup.itemCount === 1 ? s.body_one : s.body.replace('{n}', String(backup.itemCount));

  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'cf-recovery';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.hidden = false;
  toast.innerHTML = `
    <span class="cf-recovery__icon" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20"><path d="M4 10a6 6 0 1 0 1.76-4.24M4 3v3.5h3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
    </span>
    <div class="cf-recovery__body">
      <p class="cf-recovery__text"><strong></strong> <span></span></p>
      <p class="cf-recovery__source"></p>
      <div class="cf-recovery__actions">
        <button type="button" class="cf-recovery__restore"></button>
        <button type="button" class="cf-recovery__dismiss"></button>
      </div>
      <p class="cf-recovery__error" hidden></p>
    </div>`;
  toast.querySelector('strong').textContent = s.title;
  toast.querySelector('.cf-recovery__text span').textContent = body;
  toast.querySelector('.cf-recovery__source').textContent = s.source;
  const restore = toast.querySelector('.cf-recovery__restore');
  const dismiss = toast.querySelector('.cf-recovery__dismiss');
  restore.textContent = s.restore;
  dismiss.textContent = s.dismiss;

  restore.addEventListener('click', () => restoreCart(backup, restore));
  dismiss.addEventListener('click', () => {
    store.set('sessionStorage', DISMISS_KEY, { signature: backup.signature, at: Date.now() });
    track('custom_fabric_cart_recovery_dismissed', { item_count: backup.itemCount });
    hideToast();
  });
  track('custom_fabric_cart_recovery_offered', { item_count: backup.itemCount });
}

function hideToast() {
  if (toast) toast.hidden = true;
}

async function restoreCart(backup, button) {
  const s = config.strings;
  const error = toast.querySelector('.cf-recovery__error');
  error.hidden = true;
  button.disabled = true;
  button.textContent = s.restoring;

  try {
    const live = await fetchCart();
    if (live.item_count > 0) {
      saveBackup(live);
      return;
    }

    const items = backup.items.map((item) => {
      const entry = { id: item.id, quantity: item.quantity, properties: item.properties || {} };
      if (item.selling_plan) entry.selling_plan = item.selling_plan;
      return entry;
    });

    let restored = 0;
    const failures = [];
    try {
      await postJson('cart/add.js', { items });
      restored = items.length;
    } catch (batchError) {
      for (const item of items) {
        try {
          await postJson('cart/add.js', { items: [item] });
          restored += 1;
        } catch (itemError) {
          failures.push(item.id);
        }
      }
    }
    if (!restored) throw new Error('nothing restored');

    if (backup.note || (backup.attributes && Object.keys(backup.attributes).length)) {
      await postJson('cart/update.js', { note: backup.note || '', attributes: backup.attributes || {} }).catch(() => {});
    }

    track('custom_fabric_cart_recovery_restored', { item_count: restored, failed: failures.length });
    window.location.href = config.routes.cart;
  } catch (restoreError) {
    track('custom_fabric_cart_recovery_failed', { reason: String(restoreError && restoreError.message) });
    error.textContent = s.failed;
    error.hidden = false;
    button.disabled = false;
    button.textContent = s.restore;
  }
}

/* ---------------------------------------------------------------- boot */

if (config && config.enabled && !window.__cfCartRecovery) {
  window.__cfCartRecovery = true;
  hookFetch();
  hookXhr();
  bindDomSignals();

  const start = () => window.setTimeout(sync, 600);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sync();
  });
  window.addEventListener('pagehide', () => sync());
  window.setInterval(() => {
    if (document.visibilityState === 'visible') sync();
  }, SYNC_INTERVAL);
  // Theme events (Warehouse refreshes its mini cart on these)
  document.documentElement.addEventListener('product:added', () => scheduleSync(800));
  document.documentElement.addEventListener('cart:refresh', () => scheduleSync(800));
}
